/**
 * Métricas (plan 10b + 27/10): las cifras que dicen si la 2.0 funciona, ahora en el rango que elijas
 * (hoy, 7/30/90/365 días, desde el principio o fechas propias), con negocio, actividad y servicio.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { api } from '@nuvvu/shared/api';
import type { AdminMetricsOutput } from '@nuvvu/shared/api';
import { tokens } from '@nuvvu/ui';
import { call, errorText } from '../api.js';
import { card, money, muted, Notice, RangePicker, rangeOf, type Range } from '../lib/ui.js';

const eur = (c: number): string => money({ amount: c, currency: 'EUR' });

function Tile({
  label,
  value,
  sub,
  ok,
}: {
  label: string;
  value: string;
  sub?: string;
  ok?: boolean | null;
}): ReactElement {
  return (
    <div style={{ ...card, display: 'grid', gap: 4 }}>
      <span style={muted}>{label}</span>
      <strong
        style={{
          fontSize: 26,
          color: ok === undefined || ok === null ? tokens.color.fg : ok ? '#15803D' : '#C2410C',
        }}
      >
        {value}
      </strong>
      {sub && <span style={{ ...muted, fontSize: 12 }}>{sub}</span>}
    </div>
  );
}

function Group({ title, children }: { title: string; children: ReactElement[] }): ReactElement {
  return (
    <section style={{ display: 'grid', gap: 8 }}>
      <h2 style={{ margin: '8px 0 0', fontSize: 15, color: tokens.color.muted }}>{title}</h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 10,
        }}
      >
        {children}
      </div>
    </section>
  );
}

const hours = (h: number | null): string =>
  h === null ? '—' : h >= 48 ? `${(h / 24).toFixed(1)} días` : `${String(h)} h`;

export function MetricsPage(): ReactElement {
  const [range, setRange] = useState<Range>(rangeOf(30));
  const [m, setM] = useState<AdminMetricsOutput | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setM(null);
    setError(null);
    call(api.admin.metrics, { from: range.from, to: range.to })
      .then(setM)
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  }, [range]);
  return (
    <>
      <h1 style={{ margin: 0 }}>Métricas</h1>
      <RangePicker value={range} onChange={setRange} />
      <p style={muted}>
        {range.label}. Verde = objetivo del plan alcanzado; naranja = todavía no. Importes en EUR
        (otras monedas convertidas con el BCE).
      </p>
      <Notice text={error} error />
      {!m && !error && <p style={muted}>Calculando…</p>}
      {m && (
        <>
          <Group title="Negocio">
            <Tile
              label="Volumen vendido"
              value={eur(m.gmv_eur)}
              sub={`${String(m.orders_completed)} pedidos completados`}
            />
            <Tile label="Pedido medio" value={eur(m.avg_order_eur)} />
            <Tile label="Pedidos creados" value={String(m.orders_created)} />
            <Tile
              label="Cancelados"
              value={`${String(m.cancel_pct)} %`}
              sub={`${String(m.orders_cancelled)} pedidos`}
              ok={m.orders_created ? m.cancel_pct <= 10 : null}
            />
            <Tile label="Compradores" value={String(m.buyers)} />
            <Tile label="Vendedores con ventas" value={String(m.sellers)} />
          </Group>
          <Group title="Crecimiento">
            <Tile label="Usuarios nuevos" value={String(m.new_users)} />
            <Tile label="Anuncios publicados" value={String(m.listings_last_7d)} />
            <Tile label="Anuncios vendidos" value={String(m.listings_sold)} />
            <Tile label="Armarios importados" value={String(m.imports_last_7d)} />
            <Tile
              label="Anuncios con IA"
              value={`${String(m.listings_ai_pct)} %`}
              sub={`de ${String(m.listings_last_7d)} publicados`}
            />
          </Group>
          <Group title="Objetivos del plan">
            <Tile
              label="Usuarios que compran Y venden"
              value={`${String(m.buy_and_sell_pct)} %`}
              sub={`objetivo 25 % · ${String(m.active_users)} con actividad`}
              ok={m.active_users ? m.buy_and_sell_pct >= 25 : null}
            />
            <Tile
              label="Compras pagadas con saldo"
              value={`${String(m.wallet_paid_pct)} %`}
              sub="objetivo 30 %"
              ok={m.orders_completed ? m.wallet_paid_pct >= 30 : null}
            />
            <Tile
              label="Repetición a 30 días"
              value={`${String(m.repeat_30d_pct)} %`}
              sub="compradores de hace 30–60 días que volvieron"
            />
            <Tile
              label="Ventas entre países"
              value={`${String(m.cross_border_pct)} %`}
              sub={`${String(m.orders_completed)} completados`}
            />
          </Group>
          <Group title="Servicio">
            <Tile
              label="Del pago a la aceptación"
              value={hours(m.avg_accept_hours)}
              sub="media; objetivo < 24 h"
              ok={m.avg_accept_hours === null ? null : m.avg_accept_hours <= 24}
            />
            <Tile
              label="De la aceptación a la entrega"
              value={hours(m.avg_delivery_hours)}
              sub="media"
            />
            <Tile
              label="Resolución de incidencias"
              value={hours(m.dispute_resolution_hours)}
              sub={`${String(m.disputes_open)} abiertas · objetivo < 7 días`}
              ok={m.dispute_resolution_hours === null ? null : m.dispute_resolution_hours <= 168}
            />
          </Group>
          {m.top_dest_countries.length > 0 && (
            <section style={card}>
              <strong>Países de destino de los pedidos completados</strong>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                {m.top_dest_countries.map((c) => (
                  <span key={c.code}>
                    <strong>{c.code}</strong> {c.orders}
                  </span>
                ))}
              </div>
            </section>
          )}
          <p style={muted}>Calculado {new Date(m.computed_at).toLocaleString('es-ES')}.</p>
        </>
      )}
    </>
  );
}
